import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContentCard from "@/components/feature/ContentCard";
import ActorCard from "@/components/feature/ActorCard";
import EmptyState from "@/components/feature/EmptyState";
import { searchContent } from "@/lib/content";
import type { ContentType } from "@/mocks/content";

type TabKey = ContentType | "all";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "drama", label: "剧情", icon: "ri-file-text-line" },
  { key: "actor", label: "演员", icon: "ri-user-star-line" },
  { key: "news", label: "资讯", icon: "ri-newspaper-line" },
];

const suggestions = ["苏婉", "沈母", "大结局", "林知夏", "收视"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [inputValue, setInputValue] = useState(query);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setInputValue(query);
    setTab("all");
  }, [query]);

  const results = useMemo(() => searchContent(query, tab), [query, tab]);
  const allResults = useMemo(() => searchContent(query, "all"), [query]);

  const countByTab = (key: TabKey) => {
    if (key === "all") return allResults.length;
    return allResults.filter((item) => item.type === key).length;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = inputValue.trim();
    if (!keyword) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: keyword });
  };

  const title = query
    ? `搜索“${query}”的结果 | 嫂子的诱惑`
    : "站内搜索 | 嫂子的诱惑";
  const description = query
    ? `在嫂子的诱惑站内搜索到 ${allResults.length} 条与“${query}”相关的内容，包含剧情介绍、演员资料与影视资讯。`
    : "搜索嫂子的诱惑剧情章节、演员资料与影视资讯，输入关键词即可查找站内演示内容。";

  return (
    <PageShell>
      <PageMeta
        title={title}
        description={description}
        keywords={`嫂子的诱惑搜索,${query || "剧情介绍,演员资料,影视资讯"}`}
      />

      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10 lg:px-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-[32px]">
            {query ? (
              <>
                搜索“<span className="text-primary-600">{query}</span>”
              </>
            ) : (
              "站内搜索"
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            支持匹配剧情标题、演员姓名、角色名、资讯正文与标签内容。
          </p>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="flex h-11 w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 focus-within:border-primary-300">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder="输入关键词，如：苏婉、大结局、餐桌"
                aria-label="搜索关键词"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {inputValue && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setInputValue("")}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-2-line text-base" />
              搜索
            </button>
          </form>

          <p className="mt-3 text-[11px] text-foreground-400">
            <i className="ri-information-line mr-1" />
            搜索结果为站内演示数据，无后端接口支持。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12 lg:px-10">
        <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 no-scrollbar">
          {tabs.map((entry) => {
            const active = tab === entry.key;
            return (
              <button
                key={entry.key}
                type="button"
                onClick={() => setTab(entry.key)}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={`${entry.icon} text-sm`} />
                {entry.label}
                <span
                  className={`rounded-full px-1.5 text-[10px] ${
                    active ? "bg-background-50/20" : "bg-background-100 text-foreground-500"
                  }`}
                >
                  {countByTab(entry.key)}
                </span>
              </button>
            );
          })}
        </div>

        {query && (
          <p className="mt-6 text-sm text-foreground-600">
            找到 <strong className="font-semibold text-primary-600">{results.length}</strong> 条
            与“{query}”相关的内容
            <span className="ml-2 text-xs text-foreground-400">
              （共检索 {allResults.length} 条站内内容）
            </span>
          </p>
        )}

        {!query ? (
          <div className="mt-6">
            <EmptyState
              icon="ri-search-eye-line"
              title="输入关键词开始搜索"
              description="可以搜索剧情章节、演员姓名或资讯关键词，也可以试试下面的热门搜索。"
            >
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.map((keyword) => (
                  <Link
                    key={keyword}
                    to={`/search?q=${encodeURIComponent(keyword)}`}
                    className="rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {keyword}
                  </Link>
                ))}
              </div>
            </EmptyState>
          </div>
        ) : results.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon="ri-file-search-line"
              title={`没有找到与“${query}”相关的内容`}
              description="换个关键词试试，或从下面的推荐内容继续浏览。当前搜索范围为站内演示数据。"
            >
              <div className="grid w-full max-w-xl gap-2 sm:grid-cols-2">
                <Link
                  to={`/category/${encodeURIComponent("剧情介绍")}`}
                  className="flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览全部剧情介绍
                  <i className="ri-arrow-right-s-line text-base" />
                </Link>
                <Link
                  to={`/category/${encodeURIComponent("演员资料")}`}
                  className="flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览全部演员资料
                  <i className="ri-arrow-right-s-line text-base" />
                </Link>
              </div>
            </EmptyState>
          </div>
        ) : (
          <div
            className={`mt-6 grid gap-4 ${
              tab === "actor" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 md:grid-cols-2"
            }`}
          >
            {results.map((item) =>
              item.type === "actor" ? (
                <ActorCard key={item.id} actor={item} />
              ) : (
                <ContentCard key={item.id} item={item} highlight={query} />
              ),
            )}
          </div>
        )}

        <div className="mt-12 rounded-lg border border-background-200/70 bg-background-100/70 p-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-accent-700">热门搜索</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {suggestions.map((keyword) => (
              <Link
                key={`bottom-${keyword}`}
                to={`/search?q=${encodeURIComponent(keyword)}`}
                className="rounded-full bg-background-50 px-3.5 py-1.5 text-xs text-foreground-600 transition-colors hover:text-primary-700"
              >
                {keyword}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}